/**
 * UnsubscribeDashboardPage
 * ───────────────────────────────────────────────────────────────
 * Admin UI for the global email_suppressions list. Customers who
 * click "unsubscribe" in any newsletter land here automatically.
 * Staff can also bulk-add addresses (e.g. when someone replies
 * "please remove me" to a send).
 *
 * Two scopes:
 *   • marketing — newsletters + partner bulk outreach are skipped
 *     for this address. PF transactional emails STILL go through.
 *   • all       — blocks even PF emails. Use only for legal /
 *     GDPR removal requests.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Navbar } from "../components/Navbar";
import EmailCategoriesPanel from "../components/EmailCategoriesPanel";
import {
  RefreshCw, Search, Plus, Trash2, MailX, ShieldX, Upload, Info, ExternalLink,
} from "lucide-react";

export default function UnsubscribeDashboardPage() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_scope: {}, by_source: {} });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addScope, setAddScope] = useState("marketing");
  const [addReason, setAddReason] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [tab, setTab] = useState("suppressions");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/email-suppressions/list`, {
          headers,
          params: { q: query || undefined, scope: scopeFilter || undefined },
        }),
        axios.get(`${API_URL}/email-suppressions/stats`, { headers }),
      ]);
      setItems(listRes.data?.items || []);
      setStats(statsRes.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load suppressions");
    } finally {
      setLoading(false);
    }
  }, [headers, query, scopeFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addOne = async (e) => {
    e?.preventDefault?.();
    if (!addEmail.trim()) return;
    try {
      await axios.post(`${API_URL}/email-suppressions/add`, {
        email: addEmail.trim(), scope: addScope, reason: addReason || null,
      }, { headers });
      toast.success(`${addEmail.trim()} added to suppression list`);
      setAddEmail(""); setAddReason("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const bulkAdd = async () => {
    const lines = bulkText.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s.includes("@"));
    if (!lines.length) {
      toast.error("Paste at least one email address");
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/email-suppressions/bulk-import`, {
        emails: lines, scope: addScope, reason: addReason || null,
      }, { headers });
      toast.success(`Imported ${res.data.inserted} new + ${res.data.updated} updated`);
      setBulkText(""); setAddReason("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    }
  };

  const removeRow = async (email) => {
    if (!window.confirm(`Re-subscribe ${email}? They will start receiving marketing emails again.`)) return;
    try {
      await axios.delete(`${API_URL}/email-suppressions/${encodeURIComponent(email)}`, { headers });
      toast.success(`${email} removed from suppression list`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  const scopeBadge = (scope) => {
    if (scope === "all") {
      return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">ALL (incl. PFs)</span>;
    }
    return <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Marketing only</span>;
  };

  return (
    <>
      <Navbar />
      <div className="p-6 max-w-7xl mx-auto" data-testid="unsubscribe-dashboard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MailX className="h-6 w-6 text-amber-600" />
            Unsubscribe Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Global "do-not-mail" list. Applies to all marketing & newsletter sends.
            PF-related transactional emails still go through unless scope = "all".
          </p>
        </div>
        <Button variant="outline" onClick={fetchAll} disabled={loading} data-testid="unsub-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200">
        <button
          onClick={() => setTab("suppressions")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "suppressions" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-suppressions"
        >
          Suppression list
        </button>
        <button
          onClick={() => setTab("categories")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === "categories" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-categories"
        >
          Email types
        </button>
      </div>

      {tab === "categories" ? (
        <EmailCategoriesPanel />
      ) : (
      <>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">Total suppressed</div>
          <div className="text-3xl font-bold text-slate-900">{stats.total || 0}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs text-amber-700 uppercase tracking-wide">Marketing only</div>
          <div className="text-3xl font-bold text-amber-800">{stats.by_scope?.marketing || 0}</div>
          <div className="text-xs text-amber-700 mt-1">PFs still get through</div>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="text-xs text-red-700 uppercase tracking-wide">Full block</div>
          <div className="text-3xl font-bold text-red-800">{stats.by_scope?.all || 0}</div>
          <div className="text-xs text-red-700 mt-1">All emails skipped</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">By source</div>
          <div className="text-sm mt-2 space-y-0.5">
            {Object.entries(stats.by_source || {}).map(([src, n]) => (
              <div key={src} className="flex justify-between">
                <span className="text-slate-600">{src}</span>
                <span className="font-semibold">{n}</span>
              </div>
            ))}
            {!Object.keys(stats.by_source || {}).length && <span className="text-slate-400">—</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Add single */}
        <form onSubmit={addOne} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-semibold text-slate-900 mb-3 flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add one
          </div>
          <Input
            type="email"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            placeholder="email@example.com"
            data-testid="unsub-add-email"
            className="mb-2"
          />
          <Input
            value={addReason}
            onChange={(e) => setAddReason(e.target.value)}
            placeholder="Reason (optional, e.g. 'replied unsubscribe')"
            className="mb-2"
            data-testid="unsub-add-reason"
          />
          <select
            value={addScope}
            onChange={(e) => setAddScope(e.target.value)}
            className="w-full mb-2 px-3 py-2 text-sm rounded border border-slate-200"
            data-testid="unsub-add-scope"
          >
            <option value="marketing">Marketing only (PFs still go through)</option>
            <option value="all">Block ALL (incl. PF operational)</option>
          </select>
          <Button type="submit" disabled={!addEmail.includes("@")} className="w-full" data-testid="unsub-add-submit">
            Add to suppression list
          </Button>
        </form>

        {/* Bulk import */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
          <div className="font-semibold text-slate-900 mb-3 flex items-center gap-1">
            <Upload className="h-4 w-4" /> Bulk import
          </div>
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={"darja.hozjan@metrel.si\nanother@customer.com\n(paste any list separated by commas, spaces or newlines)"}
            rows={5}
            className="w-full px-3 py-2 text-sm rounded border border-slate-200 mb-2 font-mono"
            data-testid="unsub-bulk-text"
          />
          <Button onClick={bulkAdd} disabled={!bulkText.includes("@")} variant="outline" data-testid="unsub-bulk-submit">
            Import all
          </Button>
          <div className="text-xs text-slate-400 mt-2 italic">
            Bulk-added emails use the scope + reason selected on the left.
          </div>
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email…"
            className="pl-9"
            data-testid="unsub-search"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded border border-slate-200"
          data-testid="unsub-scope-filter"
        >
          <option value="">All scopes</option>
          <option value="marketing">Marketing only</option>
          <option value="all">Block ALL</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Scope</th>
              <th className="px-4 py-2 text-left">Source</th>
              <th className="px-4 py-2 text-left">Reason</th>
              <th className="px-4 py-2 text-left">Added</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No suppressed emails yet.</td></tr>
            ) : items.map((r) => (
              <tr key={r.email} className="border-t" data-testid={`unsub-row-${r.email}`}>
                <td className="px-4 py-2 font-mono">{r.email}</td>
                <td className="px-4 py-2">{scopeBadge(r.scope)}</td>
                <td className="px-4 py-2 text-slate-500 text-xs">{r.source || "—"}</td>
                <td className="px-4 py-2 text-slate-600 text-xs truncate max-w-[280px]" title={r.reason || ""}>{r.reason || "—"}</td>
                <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">
                  {(r.created_at || r.updated_at || "").slice(0, 19).replace("T", " ")}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => removeRow(r.email)}
                    className="text-red-500 hover:text-red-700"
                    title="Re-subscribe"
                    data-testid={`unsub-remove-${r.email}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 flex items-start gap-2">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          The public unsubscribe link in every marketing email points at <span className="font-mono">/api/email-suppressions/unsubscribe?email=…</span>. Customers see a friendly confirmation page. Their address is added here automatically with <b>source = link</b>.
        </div>
      </div>
      </>
      )}
      </div>
    </>
  );
}
